import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import Select, { components } from "react-select";
import { FormikProps } from "formik";
import { Badge, Button, Form } from "react-bootstrap";
import useEcwOrders from "../../../Hooks/useEcwOrders";
import EcwOrderInfo from "../InfoComponents/EcwOrderInfo";
import { ensurePeriod, toggleClass } from "../../../utils/commonUtils";
import {
  CommonEcwComponentProps,
  OrderTypeOptions,
  EcwFormValues,
  EcwOrdersResponse,
  EcwOrder,
  EcwOrderResponse,
  RecordApiMessages,
  getOrderIdDisplay,
  getOrderKey,
  RouteFieldOptionsThinj,
  SiteFieldOptions,
  findMatchedOption,
  EcwEncounterDetails,
} from "../EcwConstants";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";
import {
  setEcwOrderList,
  setEcwSelectedOrderInfo,
  setEcwOrderConfirmationStatus,
} from "../../../redux/ecwOrder/ecwOrderSlice";
import { useModulePermissions, PermissionModule, PermissionIdentifier } from "../../../utils/permissionUtils";
import EcwAddPatientModal from "./EcwAddPatientModal";
import OrderValuesConfirmationDialog from "../InfoComponents/OrderValuesConfirmationDialog";
import { toast } from "react-toastify";
import EcwEncountersModal from "../InfoComponents/EcwEncountersModal";

interface EcwBasicInfoFormProps extends CommonEcwComponentProps {
  formik: FormikProps<EcwFormValues>;
  recordApiMsgs: RecordApiMessages | null;
  ecwOrderRes: EcwOrderResponse | null;
  handleResetForm: (isReset?: boolean) => void;
}

interface getPatientOptionsParams {
  searchText?: string;
  isOptSelect?: boolean;
  fetchFromFhirApi?: boolean;
}

interface PendingThinjValues {
  routeLabel?: string;
  siteLabel?: string;
  qty?: number | string;
  unit?: string;
  routeValue?: string;
  siteValue?: string;
  qtyValue?: number;
}

const EcwBasicInfoForm: React.FC<EcwBasicInfoFormProps> = ({
  formik,
  setLoading,
  recordApiMsgs,
  ecwOrderRes,
  handleResetForm,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [ecwOrders, setEcwOrders] = useState<EcwOrdersResponse>({});
  const [showAddPatientModal, setShowAddPatientModal] = useState<boolean>(false);
  const [selectedPatientOpt, setSelectedPatientOpt] = useState<any | null>(null);
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [currentPatientId, setCurrentPatientId] = useState<string>("");
  const [searchPatientText, setSearchPatientText] = useState<string>("");
  const [isFetchFromFhirApi, setIsFetchFromFhirApi] = useState<boolean>(false);
  const [isConfirmModalOpen, setShowConfirmModalOpen] = useState<boolean>(false);
  const [isEncounterModalOpen, setIsEncounterModalOpen] = useState<boolean>(false);
  const [encounterSubmissionError, setEncounterSubmissionError] = useState<string>('');
  const [pendingThinjValues, setPendingThinjValues] = useState<PendingThinjValues>({});
  const searchTimeoutRef = useRef<any>(null);

  const {
    revertEcwOrderAPI,
    revertEcwOrderLoading,
    getEcwPatientsAPI,
    getEcwPatientsLoading,
    createEcwPatientFromFhirAPI,
    getAllEcwOrdersByPatientIdAPI,
    getAllEcwOrdersByPatientIdLoading,
    // saveAllEcwOrdersForPatientAPI,
    // saveAllEcwOrdersForPatientLoading,
    saveAllEcwOrdersByEncounterFromFhirAPI,
    saveAllEcwOrdersByEncounterFromFhirLoading,
  } = useEcwOrders();

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    setFieldTouched,
    setFieldError,
  } = formik;

  const selectedOrder = useMemo(() => {
    if (!values.orderId || !ecwOrders?.orders?.length) {
      dispatch(setEcwSelectedOrderInfo(null));
      dispatch(setEcwOrderConfirmationStatus("pending"));
      return null;
    }
    const selectedOrderInfo = ecwOrders?.orders?.find((item: EcwOrder) => {
      const itemOrderId = getOrderKey(item);
      return String(itemOrderId) === String(values.orderId);
    }) || null;
    dispatch(setEcwSelectedOrderInfo(selectedOrderInfo));
    return selectedOrderInfo;
  }, [values.orderId, ecwOrders, dispatch]);

  useEffect(() => {
    dispatch(setEcwOrderList(ecwOrders));
  }, [dispatch, ecwOrders]);

  useEffect(() => {
    setLoading?.(revertEcwOrderLoading || saveAllEcwOrdersByEncounterFromFhirLoading);
  }, [revertEcwOrderLoading, saveAllEcwOrdersByEncounterFromFhirLoading, setLoading]);

  useEffect(() => {
    if (touched.patID) {
      setFieldTouched("patID", false);
    }
  }, [values.patID]);

  useEffect(() => {
    if (touched.orderId) {
      setFieldTouched("orderId", false);
    }
  }, [values.orderId]);

  useEffect(() => {
    if (selectedPatientOpt) {
      setPatientOptions([selectedPatientOpt]);
    } else {
      setPatientOptions([]);
    }
  }, [selectedPatientOpt]);

  useEffect(() => {
    const isEmrRecordFound = patientOptions?.some(item => !item?.value?.isSRXRecord);
    setIsFetchFromFhirApi(isEmrRecordFound);
  }, [patientOptions]);

  useEffect(() => {
    if (!ecwOrderRes || !selectedOrder) return;

    const { isOrderProcessed, ecwResponseMessage } = ecwOrderRes;

    setEcwOrders((prev) => ({
      ...prev,
      orders: prev.orders?.map((order) =>
        order.id === selectedOrder.id
          ? {
            ...order,
            colour: isOrderProcessed ? "green" : "red",
            isSuccess: isOrderProcessed,
            status: isOrderProcessed ? "Administered" : order.status,
            ecwResponseMessage: isOrderProcessed ? null : ecwResponseMessage,
          }
          : order
      ),
    }));
  }, [ecwOrderRes]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const getPatientOptions = useCallback(async (params?: getPatientOptionsParams) => {
    const { searchText = "", isOptSelect = false, fetchFromFhirApi = false } = params || {};
    if (!searchText) return;

    handlePatientChange(null);
    handleOrderChange(null);
    handleResetForm(true);
    try {
      const res: any = await getEcwPatientsAPI({ searchText, fetchFromFhirApi });
      if (res && _.isArray(res) && res.length > 0) {
        const patients = res.map((item) => ({
          value: item,
          label: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode})`,
        }));
        setPatientOptions(patients);
        if (isOptSelect && patients.length) {
          const newOpt = patients[0];
          handlePatientChange(newOpt);
        }
        return patients;
      } else {
        setPatientOptions([]);
        return [];
      }
    } catch (err: any) {
      console.error(err);
      setPatientOptions([]);
      return [];
    }
  }, [getEcwPatientsAPI, handleResetForm]);

  const getEcwOrdersList = useCallback(async (patientId: string) => {
    if (!patientId) {
      setCurrentPatientId("");
      setEcwOrders({});
      return;
    }
    setCurrentPatientId(patientId);
    try {
      const res = await getAllEcwOrdersByPatientIdAPI({ patientId: patientId });
      setEcwOrders(res ?? {});
    } catch (err) {
      console.error(err);
      setEcwOrders({});
    }
  }, [getAllEcwOrdersByPatientIdAPI]);

  // const handleRetrieveLatestEcwOrders = useCallback(async () => {
  //   if (!currentPatientId) return;
  //   try {
  //     const res: any = await saveAllEcwOrdersForPatientAPI({ patientId: currentPatientId });
  //     if (res?.success) {
  //       toast.success(res?.message || res?.result || "Orders saved successfully");
  //       await getEcwOrdersList(currentPatientId);
  //     }
  //   } catch (err: any) {
  //     console.error(err);
  //   }
  // }, [currentPatientId, saveAllEcwOrdersForPatientAPI, getEcwOrdersList]);

  const handleEncounterSubmission = useCallback(async (encounter: EcwEncounterDetails | null) => {
    if (!encounter) return;

    setEncounterSubmissionError("");

    try {
      const res = await saveAllEcwOrdersByEncounterFromFhirAPI({ patientId: currentPatientId, encounter, });

      if (!res?.success) {
        setEncounterSubmissionError(res?.message || "Failed to retrieve orders.");
        return;
      }

      const orders = res.result?.orders;

      if (!orders?.length) {
        setEncounterSubmissionError("No orders found for selected encounter.");
        return;
      }

      toast.success(res.message || "Orders retrieved successfully.");
      setEcwOrders(prev => ({ ...prev, orders }));
      setIsEncounterModalOpen(false);
    } catch (error) {
      console.error("Error fetching orders for selected encounter:", error);
      setEncounterSubmissionError("Error fetching orders for selected encounter.");
    }
  }, [saveAllEcwOrdersByEncounterFromFhirAPI, currentPatientId]);

  const handleOpenEncounterModal = () => {
    if (!currentPatientId) return;
    setEncounterSubmissionError("");
    setIsEncounterModalOpen(true);
  };

  const createPatientFromFhir = async (patientInfo: any) => {
    try {
      const res: any = await createEcwPatientFromFhirAPI(patientInfo);
      if (res?.success && res?.result) {
        handleOrderChange(null);
        getEcwOrdersList(res?.result);
      }
    } catch (err: any) {
      console.error(err);
      return [];
    }
  }

  const handlePatientChange = useCallback((option: any) => {
    const patient = option?.value || null;

    setSelectedPatientOpt(option || null);

    const fields: Partial<EcwFormValues> = {
      patID: patient?.patientCode ?? "",
      patFName: patient?.firstName ?? "",
      patMName: patient?.mi ?? "",
      patLName: patient?.lastName ?? "",
      patDOB: patient?.dob ?? "",
      patGender: patient?.gender ?? "",
    };

    Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));

    if (!patient) {
      setCurrentPatientId("");
      return;
    }

    if (patient?.id) {
      setCurrentPatientId(patient.id);
    }

    if (patient.patientCode !== values.patID) {
      if (patient?.isSRXRecord) {
        handleOrderChange(null);
        getEcwOrdersList(patient.id);
      } else {
        createPatientFromFhir(patient);
      }
    }
  }, [setFieldValue, values.patID, getEcwOrdersList]);

  const toggleShowAddPatientModal = useCallback(() => {
    toggleClass();
    setShowAddPatientModal((prev) => !prev);
  }, []);

  const handlePatientAddSuccess = useCallback((patientRes: any) => {
    if (patientRes?.patientCode) {
      getPatientOptions({ searchText: patientRes.patientCode, isOptSelect: true });
      setSelectedPatientOpt({
        value: patientRes,
        label: `${patientRes.lastName}, ${patientRes.firstName} ${patientRes.mi ?? ""} (${patientRes.patientCode})`,
      });
    }
    toggleShowAddPatientModal();
  }, [getPatientOptions, toggleShowAddPatientModal]);

  const handleOrderChange = useCallback((selectedItem: any) => {
    const order = selectedItem?.value ?? null;

    // Reset auto-populated fields on every order change before doing anything
    const fields: Partial<EcwFormValues> = {
      orderId: getOrderKey(order) ?? "",
      orderType: order?.orderType ?? "",
      hcpid: order?.provider?.npi ?? "",
      hcpfName: order?.provider?.firstName ?? "",
      hcplName: order?.provider?.lastName ?? "",
      hcpNum: order?.provider?.deaNumber ?? "",
      location: order?.facility?.facilityId ? `${order?.facility?.facilityId}:${order?.facility?.facilityName}` : "",
      encounterDate: order?.encounterDate ?? "",
      encounterId: order?.encounterId ?? "",
      facilityGuid: order?.facility?.guid ?? "",
      facilityName: order?.facility?.facilityName ?? "",
      route: formik.initialValues.route,
      site: formik.initialValues.site,
      qty: formik.initialValues.qty,
      units: formik.initialValues.units,
    };

    Object.entries(fields).forEach(([key, val]) => setFieldValue(key, val));

    const resetFields = ["route", "site", "qty", "units"] as const;
    resetFields.forEach((field) => {
      setFieldTouched(field, false, false);
      setFieldError(field, undefined);
    });

    handleConfirmationFlow(order);
  }, [setFieldValue, formik.initialValues, setFieldTouched, setFieldError]);

  const handleConfirmationFlow = useCallback((order: any) => {
    if (!order) {
      setShowConfirmModalOpen(false);
      dispatch(setEcwOrderConfirmationStatus("pending"));
      return;
    }

    const isThinj = order.orderType === "THINJ" && !!order.medicationAdministration?.fhirId;
    const isProcessed = order.status?.toLowerCase() === "administered" && order.colour?.toLowerCase() === "green";

    if (isThinj && !isProcessed) {
      const medAdmin = order.medicationAdministration;
      const rawRoute = medAdmin?.route?.trim();
      const rawSite = medAdmin?.site?.trim();
      const rawDosage = medAdmin?.dosage;
      const hasQty = rawDosage != null && !isNaN(Number(rawDosage)) && Number(rawDosage) > 0;
      const hasSuppliedValues = Boolean(rawRoute || rawSite || hasQty);

      if (hasSuppliedValues) {
        const matchedRoute = findMatchedOption(rawRoute, RouteFieldOptionsThinj);
        const matchedSite = findMatchedOption(rawSite, SiteFieldOptions);

        setPendingThinjValues({
          routeLabel: matchedRoute?.value ? matchedRoute.label : undefined,
          siteLabel: matchedSite?.value ? matchedSite.label : undefined,
          qty: hasQty ? Number(rawDosage) : undefined,
          unit: medAdmin?.dosageUnit?.trim() || undefined,
          routeValue: matchedRoute?.value ?? "",
          siteValue: matchedSite?.value ?? "",
          qtyValue: hasQty ? Number(rawDosage) : 0,
        });

        dispatch(setEcwOrderConfirmationStatus("pending"));
        setShowConfirmModalOpen(true);
        return;
      }
    }

    // Non-THINJ, already processed, or THINJ with no supplied values
    setShowConfirmModalOpen(false);
    dispatch(setEcwOrderConfirmationStatus("confirmed"));
  }, [dispatch]);

  const handleConfirmation = useCallback(() => {
    dispatch(setEcwOrderConfirmationStatus("confirmed"));
    setShowConfirmModalOpen(false);

    if (pendingThinjValues.routeValue) {
      setFieldValue("route", pendingThinjValues.routeValue);
    }
    if (pendingThinjValues.siteValue) {
      setFieldValue("site", pendingThinjValues.siteValue);
    }
    if (pendingThinjValues.qtyValue) {
      setFieldValue("qty", pendingThinjValues.qtyValue);
    }
  }, [dispatch, pendingThinjValues, setFieldValue]);

  const handleCancelConfirmation = useCallback(() => {
    dispatch(setEcwOrderConfirmationStatus("cancelled"));
    setShowConfirmModalOpen(false);

    setFieldValue("route", formik.initialValues.route);
    setFieldValue("site", formik.initialValues.site);
    setFieldValue("qty", formik.initialValues.qty);
    setFieldValue("units", formik.initialValues.units);

    setFieldTouched("route", false, false);
    setFieldTouched("site", false, false);
    setFieldTouched("qty", false, false);
    setFieldTouched("units", false, false);
    setFieldError("route", undefined);
    setFieldError("site", undefined);
    setFieldError("qty", undefined);
    setFieldError("units", undefined);
  }, [dispatch, formik.initialValues, setFieldValue, setFieldTouched, setFieldError]);

  const handleRevertOrder = useCallback(async (orderId: string | number) => {
    if (!orderId) return;
    try {
      const revertRes = await revertEcwOrderAPI({ id: orderId });
      const revicedOrderInfo = revertRes?.result;

      if (revicedOrderInfo) {
        setEcwOrders((prev) => ({
          ...prev,
          orders: prev.orders?.map((order) =>
            order.id === revicedOrderInfo.id
              ? {
                ...order,
                colour: revicedOrderInfo?.colour,
                isSuccess: revicedOrderInfo?.isSuccess,
                status: revicedOrderInfo?.status,
              }
              : order
          ),
        }));
      }
    } catch (error) {
      console.error("Error occurred while reverting order:", error);
    }
  }, []);

  const handlePatientInputChange = useCallback((newValue: string) => {
    setSearchPatientText(newValue);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (newValue) {
      searchTimeoutRef.current = setTimeout(() => {
        getPatientOptions({ searchText: newValue });
      }, 300);
    } else {
      setPatientOptions([]);
    }
  }, [getPatientOptions]);

  const handleRetrieveFromEmr = useCallback(() => {
    setIsFetchFromFhirApi(true);
    getPatientOptions({ searchText: searchPatientText, fetchFromFhirApi: true });
  }, [getPatientOptions, searchPatientText]);

  const customComponents = useMemo(() => ({
    DropdownIndicator: () => null,
    Menu: (props: any) => (
      <components.Menu {...props}>
        {props.children}
        {(searchPatientText && !isFetchFromFhirApi) && (
          <div className="p-2 border-top d-flex justify-content-center">
            <Button
              size="sm"
              variant="link"
              className="p-0 m-0"
              onClick={handleRetrieveFromEmr}
              style={{ pointerEvents: getEcwPatientsLoading ? "none" : "auto" }}
            >
              Retrieve from EMR
            </Button>
          </div>
        )}
      </components.Menu>
    )
  }), [getEcwPatientsLoading, handleRetrieveFromEmr, searchPatientText, isFetchFromFhirApi]);

  const ecwOrdersOptions = useMemo(() => {
    return (
      ecwOrders?.orders?.map((item: EcwOrder) => {
        const itemName = item?.medicationRequest?.medicationName || item?.vaccineName || item?.medicationAdministration?.medicationName;
        const orderId = getOrderIdDisplay(item);
        return {
          label: `${item?.encounterDate || "NA"} - ${itemName} (Enc. Id: ${item?.encounterId}, Order Id: ${orderId})`,
          value: item,
        };
      }) || []
    );
  }, [ecwOrders?.orders]);

  const isPermitted = useModulePermissions(PermissionModule.ECW_ORDER_ADMINISTRATION);
  const isAddPatientPermitted = isPermitted(PermissionIdentifier.Add_PATIENT_ON_ECW);

  return (
    <>
      <div className="basic-info-box">
        <h4 className="form-section-title">
          Basic Information
          <span className={`order-type-symbol ${values.orderType}`}>
            {values.orderType}
          </span>
        </h4>
        <Form.Group className="form-field">
          <Form.Label>
            Patient ID*{" "}
            {isAddPatientPermitted && (
              <Badge bg="primary" onClick={toggleShowAddPatientModal}>
                Add Patient
              </Badge>
            )}
          </Form.Label>
          <Select
            value={selectedPatientOpt}
            isLoading={getEcwPatientsLoading}
            options={patientOptions}
            classNamePrefix="react-select"
            placeholder="Enter Patient ID"
            onChange={handlePatientChange}
            onBlur={() => setFieldTouched("patID", true, true)}
            onInputChange={handlePatientInputChange}
            components={customComponents}
            className={`${touched.patID && errors.patID ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.patID && touched.patID && (
            <Form.Control.Feedback type="invalid">
              {errors.patID}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>
            Order*{" "}
            <Badge
              bg={!currentPatientId ? "secondary" : "primary"}
              style={{ cursor: !currentPatientId ? "not-allowed" : "pointer" }}
              onClick={handleOpenEncounterModal}
            >
              Get Latest Orders
            </Badge>
          </Form.Label>
          <Select
            name="orderId"
            options={ecwOrdersOptions}
            isLoading={getAllEcwOrdersByPatientIdLoading}
            classNamePrefix="react-select"
            placeholder="Please select order"
            onChange={handleOrderChange}
            onBlur={() => setFieldTouched("orderId", true, true)}
            value={
              ecwOrdersOptions.find(
                (opt) => String(getOrderKey(opt.value)) === String(values.orderId)
              ) || null
            }
            className={`${touched.orderId && errors.orderId ? "is-invalid-border" : ""} react-select-container`}
          />
          {errors.orderId && touched.orderId && (
            <Form.Control.Feedback type="invalid">
              {errors.orderId}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="form-field">
          <Form.Label>Order Type</Form.Label>
          <div className="radio-group">
            {OrderTypeOptions.map((item) => (
              <Form.Check
                inline
                disabled
                key={item.value}
                type="radio"
                id={`orderType-${item.value}`}
                name="orderType"
                label={item.label}
                value={item.value}
                onChange={handleChange}
                checked={values.orderType === item.value}
              />
            ))}
          </div>
        </Form.Group>

        <EcwOrderInfo
          ecwOrderList={ecwOrders}
          selectedOrderInfo={selectedOrder}
          handleRevertOrder={handleRevertOrder}
        />

        {recordApiMsgs && (
          <ul className="d-flex flex-column">
            {recordApiMsgs.success && (
              <li className="text-success fw-semibold">
                {ensurePeriod(recordApiMsgs.success)}
              </li>
            )}
            {recordApiMsgs.error?.map((err, index) => (
              <li key={index} className="text-danger fw-semibold">
                {ensurePeriod(err)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddPatientModal && (
        <EcwAddPatientModal
          show={showAddPatientModal}
          onClose={toggleShowAddPatientModal}
          onSuccess={handlePatientAddSuccess}
          setLoading={setLoading}
        />
      )}

      {isConfirmModalOpen && (
        <OrderValuesConfirmationDialog
          show={isConfirmModalOpen}
          routeLabel={pendingThinjValues.routeLabel}
          siteLabel={pendingThinjValues.siteLabel}
          qty={pendingThinjValues.qty}
          unit={pendingThinjValues.unit}
          onConfirm={handleConfirmation}
          onCancel={handleCancelConfirmation}
        />
      )}

      {isEncounterModalOpen && (
        <EcwEncountersModal
          isOpen={isEncounterModalOpen}
          onClose={() => setIsEncounterModalOpen(false)}
          patientId={currentPatientId || ""}
          onEncounterSelect={handleEncounterSubmission}
          encounterSubmissionError={encounterSubmissionError}
        />
      )}
    </>
  );
};

export default EcwBasicInfoForm;
